import { AlertCircle, Bot, Database, Loader2, MessageSquare, Settings, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EntityDetailHeader, useToast } from "@/components/shared";
import { ConversationsTab } from "@/components/team-hub/ConversationsTab";
import { HubTab } from "@/components/team-hub/HubTab";
import { MembersTab } from "@/components/team-hub/MembersTab";
import { ResourcesTab } from "@/components/team-hub/ResourcesTab";
import { SettingsTab } from "@/components/team-hub/SettingsTab";
import type { TabId } from "@/components/team-hub/utils";
import { useAuth } from "@/context/AuthContext";
import { teamService } from "@/lib/services";
import type { TeamHub as TeamHubType } from "@/types";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TeamHub() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [hub, setHub] = useState<TeamHubType | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("hub");
  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(new Set());

  const isAdmin =
    hub?.currentUserRole === "team_admin" || hub?.currentUserRole === "platform_admin" || user?.isPlatformAdmin;

  const loadHub = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      const response = await teamService.getHub(teamId);
      setHub(response.data);
    } catch (error) {
      console.error("Failed to load team hub:", error);
      showToast("Failed to load team", "error");
    } finally {
      setLoading(false);
    }
  }, [teamId, showToast]);

  useEffect(() => {
    loadHub();
  }, [loadHub]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!hub) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-content-primary mb-2">Team not found</h2>
        <button
          onClick={() => navigate("/teams")}
          className="mt-4 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
        >
          Back to Teams
        </button>
      </div>
    );
  }

  const tabs: { id: TabId; label: string; adminOnly?: boolean }[] = [
    { id: "hub", label: "Hub" },
    { id: "conversations", label: `Conversations (${hub.stats.chatCount})` },
    { id: "resources", label: "Resources" },
    { id: "members", label: `Members (${hub.stats.memberCount})` },
    { id: "settings", label: "Settings", adminOnly: true },
  ];

  const firstName = user?.displayName?.split(" ")[0] || user?.name?.split(" ")[0] || "there";

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <EntityDetailHeader
        onBack={() => navigate("/teams")}
        icon={Users}
        name={hub.name}
        description={hub.description}
        badge={
          hub.isEveryone && (
            <span className="px-2 py-0.5 text-xs font-medium bg-brand-primary/20 text-brand-primary rounded-full shrink-0">
              Everyone
            </span>
          )
        }
        actions={
          isAdmin && (
            <button
              type="button"
              onClick={() => navigate(`/teams/${hub.uuid}/manage`)}
              className="flex items-center gap-2 px-4 py-2.5 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg font-medium transition-colors whitespace-nowrap"
            >
              <Settings className="h-4 w-4" />
              Manage
            </button>
          )
        }
      >
        {/* Stats bar */}
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-content-quaternary" />
            <span className="text-content-tertiary">
              {hub.stats.memberCount} {hub.stats.memberCount === 1 ? "member" : "members"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-content-quaternary" />
            <span className="text-content-tertiary">
              {hub.stats.agentCount} {hub.stats.agentCount === 1 ? "agent" : "agents"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-content-quaternary" />
            <span className="text-content-tertiary">
              {hub.stats.dataSourceCount} {hub.stats.dataSourceCount === 1 ? "data source" : "data sources"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-content-quaternary" />
            <span className="text-content-tertiary">
              {hub.stats.chatCount} {hub.stats.chatCount === 1 ? "conversation" : "conversations"}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-4 -mb-px" role="tablist" aria-label="Team hub sections">
          {tabs
            .filter((tab) => !tab.adminOnly || isAdmin)
            .map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`tabpanel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border transition-colors ${
                  activeTab === tab.id
                    ? "border-border-primary border-b-transparent bg-surface-primary text-content-primary"
                    : "border-transparent text-content-tertiary hover:text-content-secondary"
                }`}
              >
                {tab.label}
              </button>
            ))}
        </div>
      </EntityDetailHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === "hub" && (
          <HubTab
            hub={hub}
            navigate={navigate}
            firstName={firstName}
            dismissedNotifications={dismissedNotifications}
            onDismissNotification={(id) => setDismissedNotifications((prev) => new Set(prev).add(id))}
            onManageTasks={isAdmin ? () => setActiveTab("settings") : undefined}
            onViewAllConversations={() => setActiveTab("conversations")}
          />
        )}
        {activeTab === "conversations" && <ConversationsTab teamUuid={hub.uuid} navigate={navigate} />}
        {activeTab === "resources" && (
          <ResourcesTab hub={hub} navigate={navigate} onViewAllConversations={() => setActiveTab("conversations")} />
        )}
        {activeTab === "members" && <MembersTab hub={hub} />}
        {activeTab === "settings" && isAdmin && <SettingsTab hub={hub} navigate={navigate} onRefresh={loadHub} />}
      </div>
    </div>
  );
}
